import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PRO_BENEFITS = [
  "Unlimited weekly Planner — pick meals for every day",
  "Unlimited shopping lists (free = 1)",
  "Unlimited AI photo scans & Discover searches",
  "Auto-reorder your weekly basket",
  "Healthier basket score (coming soon)",
  "Custom calorie target & macros",
  "Weekly recap email — every Sunday",
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);
  const [me, setMe] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [applied, setApplied] = useState(null); // { code, discount_percent, is_free }
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get("/billing/plans"),
          user ? api.get("/billing/me") : Promise.resolve({ data: null }),
        ]);
        setPlans(p.data.plans);
        setMe(m.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const startCheckout = async (planId) => {
    if (!user) {
      toast.error("Please sign in first");
      return;
    }
    setBusyPlan(planId);
    try {
      const body = {
        plan_id: planId,
        origin_url: window.location.origin,
      };
      if (applied && !applied.is_free) body.code = applied.code;
      const { data } = await api.post("/billing/checkout", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusyPlan(null);
    }
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      toast.error("Enter a code");
      return;
    }
    // Use monthly plan as the validate target — it's just for showing the % off
    setCouponBusy(true);
    try {
      const { data } = await api.post("/billing/coupon/validate", { code, plan_id: "monthly" });
      setApplied({ code: data.code, discount_percent: data.discount_percent, is_free: data.is_free });
      toast.success(data.is_free ? `🎉 ${data.code} unlocks Pro free` : `${data.code} applied — ${data.discount_percent}% off`);
    } catch (e) {
      setApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setCouponBusy(false);
    }
  };

  const clearCoupon = () => {
    setApplied(null);
    setCouponCode("");
  };

  const redeemFree = async (planId) => {
    if (!applied?.is_free) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/billing/coupon/redeem-free", { code: applied.code, plan_id: planId });
      toast.success("🎉 Welcome to Shop, Cal & Meal Pro!");
      await refresh();
      window.location.href = `/app/billing/return?free=1&expires=${encodeURIComponent(data.expires_at)}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem code");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return <div className="text-[#5C6B61]">Loading plans…</div>;
  }

  const proUntil = me?.expires_at ? new Date(me.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
  const monthly = plans.find((p) => p.plan_id === "monthly");
  const cheapest = plans.reduce((min, p) => {
    const perMonth = (p.price_gbp / (p.duration_days / 30));
    return perMonth < (min?._perMonth ?? Infinity) ? { ...p, _perMonth: perMonth } : min;
  }, null);

  return (
    <div className="space-y-8" data-testid="billing-page">
      <header>
        <div className="ff-label">Subscription</div>
        <h1 className="text-4xl sm:text-5xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Shop, Cal & Meal Pro 🌱
        </h1>
        <p className="text-[#5C6B61] mt-2 max-w-2xl">
          Unlock the full app — unlimited planner, scans, shopping lists and weekly recaps.
          Cancel any time, no questions asked.
        </p>
        {me?.pro && (
          <div className="mt-4 inline-flex items-center gap-2 bg-[#E8F0E9] text-[#3B5A3E] px-4 py-2 rounded-full text-sm font-medium" data-testid="pro-active-pill">
            ✓ Pro active{proUntil ? ` — renews ${proUntil}` : ""}
          </div>
        )}
      </header>

      <div className="ff-card p-5 md:p-6" data-testid="coupon-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="ff-label">Have a discount code?</div>
            <p className="text-sm text-[#5C6B61] mt-1">Apply it to see the new price on each plan.</p>
          </div>
          {applied ? (
            <div className="flex items-center gap-3" data-testid="coupon-applied">
              <span className="bg-[#E8F0E9] text-[#3B5A3E] text-xs font-medium px-3 py-1.5 rounded-full">
                {applied.is_free ? `🎁 ${applied.code} — FREE` : `✓ ${applied.code} · ${applied.discount_percent}% off`}
              </span>
              <button
                onClick={clearCoupon}
                className="text-xs text-[#A65541] hover:underline"
                data-testid="coupon-clear-btn"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                className="ff-input text-sm"
                data-testid="coupon-input"
                onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
              />
              <button
                onClick={applyCoupon}
                disabled={couponBusy}
                data-testid="coupon-apply-btn"
                className="ff-btn-primary text-sm disabled:opacity-60"
              >
                {couponBusy ? "Checking…" : "Apply"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5" data-testid="plan-grid">
        {plans.map((p) => {
          const isCheapest = cheapest?.plan_id === p.plan_id;
          const perMonth = (p.price_gbp / (p.duration_days / 30)).toFixed(2);
          const savings = monthly && p.plan_id !== "monthly"
            ? Math.max(0, Math.round((1 - p.price_gbp / (monthly.price_gbp * (p.duration_days / 30))) * 100))
            : 0;
          const discounted = applied
            ? Math.max(0, Number((p.price_gbp * (100 - applied.discount_percent) / 100).toFixed(2)))
            : null;
          return (
            <div
              key={p.plan_id}
              className={`ff-card p-6 relative ${isCheapest ? "ring-2 ring-[#3B5A3E]" : ""}`}
              data-testid={`plan-card-${p.plan_id}`}
            >
              {isCheapest && (
                <div className="absolute -top-3 left-6 text-[10px] font-medium uppercase tracking-wider bg-[#3B5A3E] text-white px-2 py-1 rounded-full">
                  Best value
                </div>
              )}
              <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{p.label}</h3>
              <p className="text-xs text-[#8B9690] mt-1">{p.subtitle}</p>
              <div className="mt-4 flex items-baseline gap-2">
                {discounted != null && discounted !== p.price_gbp ? (
                  <>
                    <span className="text-4xl font-semibold text-[#2A332C]" data-testid={`plan-final-${p.plan_id}`}>
                      £{discounted.toFixed(2)}
                    </span>
                    <span className="text-sm text-[#8B9690] line-through">£{p.price_gbp.toFixed(2)}</span>
                  </>
                ) : (
                  <span className="text-4xl font-semibold text-[#2A332C]">£{p.price_gbp.toFixed(2)}</span>
                )}
                <span className="text-sm text-[#8B9690]">/{p.plan_id === "monthly" ? "month" : p.plan_id === "yearly" ? "year" : "6 months"}</span>
              </div>
              {p.plan_id !== "monthly" && (
                <p className="text-xs text-[#5C6B61] mt-1">
                  ≈ £{perMonth} / month{savings > 0 ? ` · save ${savings}%` : ""}
                </p>
              )}
              {applied?.is_free ? (
                <button
                  onClick={() => redeemFree(p.plan_id)}
                  disabled={redeeming}
                  data-testid={`redeem-free-${p.plan_id}`}
                  className="w-full mt-5 rounded-full px-5 py-3 text-sm font-medium bg-[#3B5A3E] text-white hover:bg-[#2c4530] transition disabled:opacity-60"
                >
                  {redeeming ? "Activating…" : "🎁 Activate free Pro"}
                </button>
              ) : (
                <button
                  onClick={() => startCheckout(p.plan_id)}
                  disabled={busyPlan === p.plan_id}
                  data-testid={`subscribe-${p.plan_id}`}
                  className={`w-full mt-5 rounded-full px-5 py-3 text-sm font-medium transition disabled:opacity-60 ${
                    isCheapest ? "bg-[#3B5A3E] text-white hover:bg-[#2c4530]" : "bg-[#2A332C] text-white hover:bg-[#1d2521]"
                  }`}
                >
                  {busyPlan === p.plan_id ? "Redirecting…" : me?.pro ? "Extend Pro" : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <section className="ff-card p-6 md:p-8">
        <div className="ff-label">What&apos;s included</div>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Everything in Pro
        </h2>
        <ul className="mt-4 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-[#2A332C]">
          {PRO_BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span className="text-[#3B5A3E] mt-0.5">✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-xs text-[#8B9690]">
        Payments handled securely by Stripe. By subscribing you agree to be billed for the period selected.
        Cancel any time from <Link to="/app/profile" className="underline">Profile</Link>.
      </p>
    </div>
  );
}
